import { Fragment } from 'react';
import { clsx } from 'clsx';
import type { TextAnalyticsView, TextFunnelStep, TextStepKey } from './types';
import { formatDuration, percent } from './format';
import styles from './analytics.module.css';

const PHASE_LABEL: Record<TextFunnelStep['phase'], string> = {
  before: 'Before the first text',
  conversation: 'The conversation',
  handoff: 'Draft and hand-off',
};

function Row({
  index,
  label,
  hint,
  count,
  first,
  drop,
  hot,
  active,
  onClick,
}: {
  index: number;
  label: string;
  hint: string;
  count: number;
  first: number;
  drop: number | null;
  hot: boolean;
  active: boolean;
  onClick?: () => void;
}) {
  const width = first > 0 ? Math.round((count / first) * 100) : 0;
  const content = (
    <>
      <span className={styles.stepIndex}>{index}</span>
      <span>
        <span className={styles.stepLabel}>{label}</span>
        <span className={styles.stepHint}>{hint}</span>
      </span>
      <span className={styles.track}>
        <span className={styles.bar} style={{ width: `${width}%` }} />
      </span>
      <span className={styles.numStrong}>{count}</span>
      <span className={styles.pctCell}>{first > 0 ? `${width}%` : '—'}</span>
      <span className={hot ? styles.numHot : styles.numMuted}>
        {drop === null ? '–' : `−${drop}`}
      </span>
    </>
  );
  return onClick ? (
    <button
      type="button"
      className={clsx(styles.funnelRow, active && styles.funnelRowActive)}
      onClick={onClick}
      aria-pressed={active}
    >
      {content}
    </button>
  ) : (
    <div className={styles.funnelRow}>{content}</div>
  );
}

/**
 * The text funnel: every confirmed visit, step by step, to a review live on Google. The full
 * version (Command Center) groups steps by phase, shows the 5★/≤4★ branch and lets a step be
 * selected for its detail. `keys` picks a shorter ladder for the practice view.
 */
export function TextFunnel({
  view,
  selected,
  onSelect,
  keys,
}: {
  view: TextAnalyticsView;
  selected?: TextStepKey;
  onSelect?: (key: TextStepKey) => void;
  keys?: TextStepKey[];
}) {
  const first = view.steps[0]?.count ?? 0;
  if (first === 0) {
    return (
      <p className={styles.muted}>
        The funnel fills in once visits are confirmed and texts go out.
      </p>
    );
  }

  if (keys) {
    // Practice ladder: the drop is between the rows shown, and "Gave 5★" stands in for the branch.
    const rows = keys
      .map((key) => {
        const step = view.steps.find((s) => s.key === key);
        if (!step) return null;
        return key === 'rated'
          ? {
              key,
              label: 'Happy (5★)',
              hint: `${view.branch.lowStar} rated ≤4★ and were asked privately`,
              count: view.branch.fiveStar,
            }
          : { key, label: step.label, hint: step.hint, count: step.count };
      })
      .filter((r): r is NonNullable<typeof r> => r !== null);
    return (
      <div>
        <div className={styles.funnelHead}>
          <span />
          <span>Step</span>
          <span />
          <span>Count</span>
          <span>Of first</span>
          <span>Drop</span>
        </div>
        {rows.map((r, i) => (
          <Row
            key={r.key}
            index={i + 1}
            label={r.label}
            hint={r.hint}
            count={r.count}
            first={first}
            drop={i === 0 ? null : Math.max(0, rows[i - 1].count - r.count)}
            hot={false}
            active={false}
          />
        ))}
      </div>
    );
  }

  const biggest = view.steps.find((s) => s.key === view.biggestDropStep);
  return (
    <div>
      {biggest && biggest.lost > 0 && (
        <div className={styles.dropBanner}>
          Biggest drop-off after the first reply is <b>{biggest.label}</b>: <b>{biggest.lost}</b> of{' '}
          {biggest.eligible} patients ({percent(biggest.lost, biggest.eligible)}) stopped here.
        </div>
      )}
      <div className={styles.funnelHead}>
        <span />
        <span>Step</span>
        <span />
        <span>Count</span>
        <span>Of first</span>
        <span>Drop</span>
      </div>
      {view.steps.map((s, i) => (
        <Fragment key={s.key}>
          {(i === 0 || view.steps[i - 1].phase !== s.phase) && (
            <div className={styles.phase}>{PHASE_LABEL[s.phase]}</div>
          )}
          <Row
            index={i + 1}
            label={s.label}
            hint={
              s.medianSecondsFromPrev !== null && i > 0
                ? `${s.hint} · ${formatDuration(s.medianSecondsFromPrev)} median`
                : s.hint
            }
            count={s.count}
            first={first}
            drop={i === 0 ? null : s.lost}
            hot={s.key === view.biggestDropStep}
            active={selected === s.key}
            onClick={onSelect ? () => onSelect(s.key) : undefined}
          />
          {s.key === 'rated' && (
            <div className={styles.branch}>
              <span className={styles.branchTag}>BRANCH</span>
              <span>
                <b>{view.branch.fiveStar}</b> gave 5★ → Betty offers to help write it
              </span>
              <span>·</span>
              <span>
                <b>{view.branch.lowStar}</b> gave ≤4★ → asked privately what went wrong (
                <b>{view.branch.privateFromLowStar}</b> answered)
              </span>
            </div>
          )}
        </Fragment>
      ))}
    </div>
  );
}

/** What happened at one step, and where the patients who stopped there went. */
export function StepDetail({ view, stepKey }: { view: TextAnalyticsView; stepKey: TextStepKey }) {
  const index = view.steps.findIndex((s) => s.key === stepKey);
  const step = view.steps[index];
  if (!step) return null;
  return (
    <div className={styles.card}>
      <div className={styles.eyebrow}>Step detail</div>
      <h3 className={styles.detailTitle}>{step.label}</h3>
      <p className={styles.muted}>{step.hint}</p>
      <dl className={styles.dl}>
        <dt>Reached</dt>
        <dd>{step.count}</dd>
        <dt>Could reach</dt>
        <dd>{step.eligible}</dd>
        <dt>Of those</dt>
        <dd>{step.pctOfEligible === null ? '—' : `${step.pctOfEligible}%`}</dd>
        {index > 0 && (
          <>
            <dt>Lost here</dt>
            <dd className={step.lost > 0 ? styles.ddBad : undefined}>{step.lost}</dd>
          </>
        )}
        {step.medianSecondsFromPrev !== null && index > 0 && (
          <>
            <dt>{step.medianLabel ?? 'Median time from previous'}</dt>
            <dd>{formatDuration(step.medianSecondsFromPrev)}</dd>
          </>
        )}
      </dl>
      {step.exits.length > 0 && (
        <div className={styles.exits}>
          <div className={styles.eyebrow}>
            {step.key === 'sent' ? 'Why no text went out' : 'Where the rest went'}
          </div>
          {step.exits.map((e) => (
            <div key={e.reason} className={styles.exitRow}>
              <span>{e.reason}</span>
              <b>{e.count}</b>
            </div>
          ))}
        </div>
      )}
      {view.outcomesTruncated && index > 2 && (
        <p className={styles.note}>
          This location has more conversations than we can read at once; reasons for the oldest are
          approximate.
        </p>
      )}
      {!view.outcomesAvailable && index > 2 && (
        <p className={styles.note}>
          Betty’s conversation records couldn’t be reached, so reasons are approximate.
        </p>
      )}
    </div>
  );
}
